// MP135 G2 — Fallback UI rendered by the Sentry ErrorBoundary when an
// uncaught React error bubbles out of <App />. Without this, the SPA
// blank-screens on a single component throw — users see nothing and
// have no way to recover short of refreshing.
//
// Kept dependency-free (no Card / Button imports from @cyber-aware/ui)
// so even if the UI package itself is what threw, this screen renders.
// Inline styles for the same reason — Tailwind classes work but the
// hardcoded fallback is safer if a CSS pipeline breakage caused the
// crash.

interface Props {
  error: unknown;
  resetError: () => void;
  eventId?: string;
}

export function FallbackErrorScreen({ error, resetError, eventId }: Props) {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: 'rgb(248, 250, 252)',
        color: 'rgb(15, 23, 42)',
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          background: 'white',
          borderRadius: 12,
          padding: '2rem',
          boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
          border: '1px solid rgb(226, 232, 240)',
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'rgb(190, 18, 60)',
            marginBottom: 8,
          }}
        >
          Something broke in the X-One shell.
        </div>
        <h1 style={{ fontSize: 18, margin: '0 0 1rem' }}>We've logged the crash.</h1>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'rgb(71, 85, 105)' }}>
          The error has been reported to our team. You can try the action again or refresh
          the page; if the problem keeps happening, contact support with the reference
          below.
        </p>
        <pre
          style={{
            background: 'rgb(241, 245, 249)',
            border: '1px solid rgb(226, 232, 240)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            fontSize: 12,
            overflow: 'auto',
            margin: '1rem 0',
            color: 'rgb(51, 65, 85)',
          }}
        >
          {message}
        </pre>
        {eventId && (
          <p style={{ fontSize: 12, color: 'rgb(100, 116, 139)', margin: '0 0 1rem' }}>
            Reference: <code>{eventId}</code>
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={resetError}
            style={{
              background: 'rgb(15, 23, 42)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '0.5rem 1rem',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'white',
              color: 'rgb(15, 23, 42)',
              border: '1px solid rgb(226, 232, 240)',
              borderRadius: 6,
              padding: '0.5rem 1rem',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}
